import { useState, type FormEvent } from 'react';
import { API_BASE, sessionStore } from '../api/client';
import { useRequestCode, useVerifyCode } from '../api/hooks';
import { Button, ErrorNote, Field, SuccessNote } from '../ui';
import { Wordmark } from './Shell';
import s from './Shell.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ENDED = { expired: '後台登入已到期，請重新登入。' } as const;

/** Email one-time code only: request a code, then verify it. Sessions last ADMIN_SESSION_HOURS (a week by default). */
export function SignIn() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const request = useRequestCode();
  const verify = useVerifyCode();
  const ended = sessionStore.endedReason();

  const normalized = email.trim().toLowerCase();

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(normalized)) return;
    request.mutate(normalized, {
      onSuccess: () => {
        setStep('code');
        setCode('');
        verify.reset();
      },
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return;
    verify.mutate({ email: normalized, code });
  };

  return (
    <div className={s.center}>
      <div className={s.panel}>
        <div className={s.panelBrand}>
          <Wordmark className="" />
          <span className="small faint" style={{ letterSpacing: '0.3em', fontWeight: 800 }}>
            營運後台
          </span>
        </div>

        {ended && step === 'email' && <p className="small muted">{ENDED[ended]}</p>}

        {step === 'email' ? (
          <form className="stack" onSubmit={submitEmail}>
            <h1>登入</h1>
            <Field label="電郵" hint="我們會寄出六位數字的一次性驗證碼。">
              {(id) => <input id={id} type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />}
            </Field>
            {request.isError && <ErrorNote error={request.error} />}
            <Button type="submit" variant="primary" busy={request.isPending} disabled={!EMAIL_RE.test(normalized)} style={{ height: 42 }}>
              寄出驗證碼
            </Button>
          </form>
        ) : (
          <form className="stack" onSubmit={submitCode}>
            <h1>輸入驗證碼</h1>
            <SuccessNote>
              驗證碼已寄到 <strong>{normalized}</strong>。開發環境下驗證碼會印在 API log。
            </SuccessNote>
            <Field label="六位數字驗證碼">
              {(id) => (
                <input
                  id={id}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mono"
                  style={{ letterSpacing: '0.3em', fontSize: 'var(--fs-lg)' }}
                />
              )}
            </Field>
            {verify.isError && <ErrorNote error={verify.error} />}
            <Button type="submit" variant="primary" busy={verify.isPending} disabled={code.length !== 6} style={{ height: 42 }}>
              驗證並登入
            </Button>
            <div className="spread small">
              <Button variant="ghost" size="sm" onClick={() => setStep('email')}>
                ← 改用其他電郵
              </Button>
              <Button variant="ghost" size="sm" busy={request.isPending} onClick={() => request.mutate(normalized)}>
                重新寄出
              </Button>
            </div>
          </form>
        )}
        <p className="faint small">
          只限有後台角色的帳戶。閒置一小時或登入 12 小時後自動登出。API：<code>{API_BASE}</code>
        </p>
      </div>
    </div>
  );
}
